/**
 * Sport Theme Helper
 *
 * Derives a small palette (hover/active/muted) from a base hex color.
 * The palette is applied via CSS variables at the app root.
 */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

interface Hsl {
  h: number;
  s: number;
  l: number;
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function hexToRgb(hex: string): Rgb | null {
  if (!hex || typeof hex !== 'string') return null;
  const raw = hex.trim().replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (full.length !== 6) return null;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if (![r, g, b].every((v) => Number.isFinite(v))) return null;
  return { r, g, b };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const toHex = (v: number) => Math.round(v).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const srgb = [r, g, b].map((v) => v / 255).map((c) => {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0]! + 0.7152 * srgb[1]! + 0.0722 * srgb[2]!;
}

function pickOnColor(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  const lum = relativeLuminance(rgb);
  return lum > 0.62 ? '#111827' : '#ffffff';
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s, l };
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const Hp = (h % 360) / 60;
  const X = C * (1 - Math.abs((Hp % 2) - 1));

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (Hp >= 0 && Hp < 1) [r1, g1, b1] = [C, X, 0];
  else if (Hp >= 1 && Hp < 2) [r1, g1, b1] = [X, C, 0];
  else if (Hp >= 2 && Hp < 3) [r1, g1, b1] = [0, C, X];
  else if (Hp >= 3 && Hp < 4) [r1, g1, b1] = [0, X, C];
  else if (Hp >= 4 && Hp < 5) [r1, g1, b1] = [X, 0, C];
  else if (Hp >= 5 && Hp < 6) [r1, g1, b1] = [C, 0, X];

  const m = l - C / 2;
  return {
    r: (r1 + m) * 255,
    g: (g1 + m) * 255,
    b: (b1 + m) * 255,
  };
}

function adjustLightness(hex: string, deltaL: number): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const hsl = rgbToHsl(rgb);
  const next = { ...hsl, l: clamp01(hsl.l + deltaL) };
  return rgbToHex(hslToRgb(next));
}

/**
 * Returns a CSS variables object suitable for inline style.
 * Keys: --sport-color, --sport-color-hover, --sport-color-active, --sport-color-muted,
 * --sport-on-color, and optional --sport-color-rgb, --sport-color-hover-rgb, --sport-color-active-rgb.
 */
export function deriveSportThemeCssVars(baseHex: string | null | undefined): Record<string, string> | null {
  const base = typeof baseHex === 'string' ? baseHex : null;
  if (!base) return null;

  const hover = adjustLightness(base, -0.06) || base;
  const active = adjustLightness(base, -0.12) || hover;
  const muted = adjustLightness(base, 0.32) || base;

  const baseRgb = hexToRgb(base);
  const hoverRgb = hexToRgb(hover);
  const activeRgb = hexToRgb(active);

  return {
    '--sport-color': base,
    '--sport-color-hover': hover,
    '--sport-color-active': active,
    '--sport-color-muted': muted,
    '--sport-on-color': pickOnColor(base),
    ...(baseRgb ? { '--sport-color-rgb': `${baseRgb.r} ${baseRgb.g} ${baseRgb.b}` } : {}),
    ...(hoverRgb ? { '--sport-color-hover-rgb': `${hoverRgb.r} ${hoverRgb.g} ${hoverRgb.b}` } : {}),
    ...(activeRgb ? { '--sport-color-active-rgb': `${activeRgb.r} ${activeRgb.g} ${activeRgb.b}` } : {}),
  };
}
